import React from 'react';
import { PARENT_URL } from '@apps/config';
import { RecommendCarIcons } from '@apps/config/icons.config';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import classNames from 'classnames';
import Link from 'next/link';

import { ContentBody } from '../Content/styles';
import StyledImage from '../Image';

import { ImageContentProps } from './props';
import {
  ContentSkeletonWrapper,
  ContentWrapper,
  FlashWrapper,
  ImageHoverWrapper,
  ImageWrapper,
  ParentWrapper,
  RowWrapper,
  StyledDescription,
  StyledTitle,
  StyledTitleTag,
  TitleSkeletonWrapper,
  UsedContainer,
  Wrapper,
} from './styles';

export const RecommendCarCard: React.FC<ImageContentProps> = (props) => {
  const {
    imagePosition = 'top',
    mobileImagePostion = 'top',
    tabletImagePosition = 'top',
    data,
    simpleMobileView = false,
    maxImageWidth,
    maxTabletImageWidth,
    loading,
    displayImage = true,
    imageProps,
    webView = false,
  } = props;
  const {
    price,
    title,
    location,
    mileage,
    monthlyPayment,
    warranty,
    year,
    slug,
    id,
    is_automall = false,
  } = data || {};
  const { clickAction } = usePostMsgContext();
  const href = `${PARENT_URL}/new-cars/${slug}/${id}`;

  const hasImage = !!imageProps?.src;
  const flexDirection =
    imagePosition === 'right'
      ? 'row-reverse'
      : imagePosition === 'left'
      ? 'row'
      : 'column';
  const tabletDirection =
    tabletImagePosition === 'right'
      ? 'row-reverse'
      : tabletImagePosition === 'left'
      ? 'row'
      : 'column';
  const mobileDirection =
    mobileImagePostion === 'right'
      ? 'row-reverse'
      : mobileImagePostion === 'left'
      ? 'row'
      : 'column';
  const simpleMbView = simpleMobileView;

  const onClick = (event: { preventDefault: () => void }) => {
    if (!webView) return;
    event.preventDefault();
    clickAction(href, false, {
      title,
      params: {
        id,
        action: 'car-detail-deep-link',
        isAutomall: is_automall,
      },
    });
  };

  if (loading) {
    return (
      <Wrapper
        $flexDirection={flexDirection}
        $mobileDirection={mobileDirection}
        $tabletDirection={tabletDirection}
      >
        <ImageWrapper
          $aspectRatio={(imageProps?.width ?? 1) / (imageProps?.height ?? 0.8)}
          $width={imagePosition !== 'left' ? `100%` : `${imageProps?.width}px`}
          $tabletWidth={tabletImagePosition === 'top' ? '100%' : '30%'}
          $mobileWidth={'100%'}
          $maxWidth={maxImageWidth ? maxImageWidth : undefined}
          $maxLgWidth={maxTabletImageWidth}
        >
          <div
            className={classNames('display-none', {
              'display-block skeleton skeleton-overlay': loading,
            })}
          />
        </ImageWrapper>
        <ContentSkeletonWrapper className="content">
          <ContentBody>
            <TitleSkeletonWrapper style={{ width: '100%' }}>
              <div
                className="skeleton skeleton-title-text"
                style={{
                  minWidth: '100%',
                }}
              />
            </TitleSkeletonWrapper>
            <RowWrapper $gap={8} style={{ padding: 0, width: '100%' }}>
              <div
                className="skeleton skeleton-description-text"
                style={{
                  height: 22,
                  width: '33%',
                }}
              />
              <div
                className="skeleton skeleton-description-text"
                style={{
                  height: 22,
                  width: '33%',
                }}
              />
            </RowWrapper>
            <RowWrapper $gap={10} style={{ padding: 0, width: '100%' }}>
              <div
                className="skeleton skeleton-description-text"
                style={{
                  height: 22,
                  width: '20%',
                }}
              />
              <div
                className="skeleton skeleton-description-text"
                style={{
                  height: 22,
                  width: '33%',
                }}
              />
              <div
                className="skeleton skeleton-description-text"
                style={{
                  height: 22,
                  width: '33%',
                }}
              />
            </RowWrapper>
            <div
              className="skeleton skeleton-description-text"
              style={{
                height: 22,
                width: '100%',
              }}
            />
          </ContentBody>
        </ContentSkeletonWrapper>
      </Wrapper>
    );
  }

  // ======================= VIEWS
  return (
    <Wrapper
      $flexDirection={flexDirection}
      $mobileDirection={mobileDirection}
      $tabletDirection={tabletDirection}
    >
      {hasImage && displayImage && (
        <ImageWrapper
          $aspectRatio={(imageProps?.width ?? 1) / (imageProps?.height ?? 0.8)}
          $width={imagePosition !== 'left' ? `100%` : `${imageProps?.width}px`}
          $tabletWidth={tabletImagePosition === 'top' ? '100%' : '30%'}
          $mobileWidth={simpleMbView ? '60%' : '100%'}
          $maxWidth={maxImageWidth ? maxImageWidth : undefined}
          $maxLgWidth={
            tabletImagePosition === 'top' ? '100%' : maxTabletImageWidth
          }
        >
          <div
            className={classNames('display-none', {
              // 'display-block loader': loading,
            })}
          />
          <div
            className={classNames('display-none', {
              'display-block skeleton-overlay': loading,
            })}
          />
          <FlashWrapper>
            <div style={{ position: 'relative', width: 10, height: 15 }}>
              {RecommendCarIcons.flash}
            </div>
          </FlashWrapper>
          {/* <BadgeRow>
            <Badge>

              <SocialIconButton isSvg onClick={() => {}}>
                {RecommendCarIcons.related}
              </SocialIconButton>
            </Badge>
            <Badge style={{ padding: '5.5px 8.5px 2.5px 8.5px' }}>

              <SocialIconButton isSvg onClick={() => {}}>
                {RecommendCarIcons.heart}
              </SocialIconButton>
            </Badge>
          </BadgeRow> */}

          <Link
            href={href}
            className={classNames('', {
              'display-none': loading,
            })}
            onClick={onClick}
          >
            <ImageHoverWrapper>
              <StyledImage {...imageProps} />
            </ImageHoverWrapper>
          </Link>
        </ImageWrapper>
      )}
      <ContentWrapper>
        <div>
          <Link className={'url-ani-2'} href={href} onClick={onClick}>
            <StyledTitle className="url-ani-2">{title}</StyledTitle>
          </Link>
        </div>
        <ParentWrapper $gap={8}>
          {!!warranty && warranty > 0 && (
            <RowWrapper $backgroundColor="#E9ECF3" $padding="2px 8px">
              <StyledTitleTag $fontSize={12} style={{ fontWeight: 500 }}>
                {warranty}Y Warranty
              </StyledTitleTag>
              {RecommendCarIcons.warranty}
            </RowWrapper>
          )}
          {year && (
            <RowWrapper $backgroundColor="#E9ECF3" $padding="2px 8px">
              <StyledTitleTag $fontSize={12} style={{ fontWeight: 500 }}>
                {year} Certified
              </StyledTitleTag>
              {RecommendCarIcons.certified}
            </RowWrapper>
          )}
        </ParentWrapper>
        <RowWrapper
          $gap={10}
          style={{ borderRadius: 0, maxWidth: '100%', overflow: 'hidden' }}
        >
          <UsedContainer>
            <StyledDescription $whiteText style={{ fontWeight: 700 }}>
              Used
            </StyledDescription>
          </UsedContainer>
          <RowWrapper>
            {RecommendCarIcons.mileage}
            <StyledDescription>{mileage}</StyledDescription>
          </RowWrapper>
          <RowWrapper>
            {RecommendCarIcons.location}
            <StyledDescription>{location}</StyledDescription>
          </RowWrapper>
        </RowWrapper>
        <ParentWrapper style={{ rowGap: 0 }}>
          <StyledDescription
            style={{
              color: '#31ACBF',
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1rem',
            }}
          >
            RM{' '}
            {Intl.NumberFormat('en-us').format(Number(price)).toLocaleString()}
          </StyledDescription>
          <StyledDescription
            style={{
              marginRight: 'auto',
              fontSize: 11,
              lineHeight: '12.89px',
              marginTop: 'auto',
            }}
          >
            RM{' '}
            {Intl.NumberFormat('en-us')
              .format(Number(monthlyPayment))
              .toLocaleString()}{' '}
            / Month
          </StyledDescription>
        </ParentWrapper>
      </ContentWrapper>
    </Wrapper>
  );
};

// ======================= EXPORT
export * from './props';
export default RecommendCarCard;
